import { useEffect, useRef } from 'react';
import { useScopedStorage } from '@/hooks/useScopedStorage';
import { useChatStore } from '@/stores/chatStore';
import type { Conversation } from '@/services/ai/types';

const CHAT_STORAGE_KEY = 'ai-chat-conversations';

interface PersistedChatState {
  conversations: Conversation[];
  activeId: string | null;
}

export default function ChatPersistenceSync() {
  const storage = useScopedStorage();
  const conversations = useChatStore(s => s.conversations);
  const activeConversationId = useChatStore(s => s.activeConversationId);
  const isStreaming = useChatStore(s => s.isStreaming);
  const prevRef = useRef(conversations);
  const loadedRef = useRef(false);

  // Load persisted state on mount
  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;

    const persisted = storage.getItem<PersistedChatState>(CHAT_STORAGE_KEY);
    if (persisted?.conversations?.length) {
      useChatStore.setState({
        conversations: persisted.conversations,
        activeConversationId: persisted.activeId,
      });
    }
  }, [storage]);

  // Persist on change (debounced, skip during streaming)
  useEffect(() => {
    if (isStreaming) return;
    if (conversations === prevRef.current) return;
    prevRef.current = conversations;

    const timer = setTimeout(() => {
      storage.setItem<PersistedChatState>(CHAT_STORAGE_KEY, {
        conversations,
        activeId: activeConversationId,
      });
    }, 500);

    return () => clearTimeout(timer);
  }, [conversations, activeConversationId, isStreaming, storage]);

  return null;
}
